import { FC, ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';

import AppButton from '@/shared/ui/AppButton';

import {
  ButtonContainer,
  CloseButton,
  DialogHeader,
  DialogMessage,
  StyledDialog,
} from './ConfirmationDialog.styles';

interface Props {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  title: string;
  description: string | ReactNode;
  buttonsDisabled?: boolean;
  cancelLabel?: string;
  confirmLabel?: string;
  /** Already-translated text the confirm button shows while `buttonsDisabled` (e.g. a progress count). */
  confirmLoadingText?: string;
  'data-testid'?: string;
}

const ConfirmationDialog: FC<Props> = ({
  open,
  handleClose,
  title,
  description,
  handleConfirm,
  buttonsDisabled,
  cancelLabel,
  confirmLabel,
  confirmLoadingText,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-testid={dataTestId}
    >
      <CloseButton
        onClick={handleClose}
        aria-label={t('buttons.close')}
        data-testid={dataTestId && `${dataTestId}-close`}
      >
        <CloseIcon />
      </CloseButton>

      <DialogHeader id={titleId}>{title}</DialogHeader>
      <DialogMessage id={descriptionId}>{description}</DialogMessage>

      <ButtonContainer>
        <AppButton
          variant="ghost"
          onClick={handleClose}
          disabled={!open || buttonsDisabled}
          data-testid={dataTestId && `${dataTestId}-cancel`}
        >
          {t(cancelLabel || 'buttons.cancel')}
        </AppButton>
        <AppButton
          variant="primary"
          onClick={handleConfirm}
          disabled={!open}
          loading={buttonsDisabled}
          data-testid={dataTestId && `${dataTestId}-confirm`}
        >
          {buttonsDisabled && confirmLoadingText
            ? confirmLoadingText
            : t(confirmLabel || 'buttons.yes')}
        </AppButton>
      </ButtonContainer>
    </StyledDialog>
  );
};

export default ConfirmationDialog;
